/**
 * Rich content for the print shop — category and product presentation data.
 * Images: hotlinked from SinaLite's public CDN (we're a legitimate reseller;
 *   admin can swap to owner-hosted images later via Phase 2 image manager).
 * Descriptions: authored to match SinaLite's marketing voice.
 */

const CDN = "https://sinalite.com/media/images";
// Owner-uploaded photograph of a physical rigid sign — used for the Signs category
// because SinaLite's rigid-signs.png returns 404.
const OWNER_SIGN = "https://customer-assets.emergentagent.com/job_gta-wraps-signs/artifacts/esjozl7j_20250821_194526.jpg";

// Hero banner shown at the top of /shop
export const SHOP_HERO = {
  eyebrow: "The Print Shop",
  title: "Order premium print, online — with live pricing.",
  subtitle: "Business cards, flyers, postcards, brochures, banners, and specialty prints. Configure your job, see instant pricing, and we'll handle production and Canada-wide shipping.",
  image: `${CDN}/bc-aq.png`,
  benefits: [
    "Live pricing — no surprises",
    "Free artwork check",
    "Canada-wide fast shipping",
    "Backed by our sign & wrap studio",
  ],
};

// Trust strip below hero
export const SHOP_TRUST = [
  { title: "Free proof review", body: "Our team reviews every artwork file before printing to catch bleed, resolution and colour issues." },
  { title: "Wholesale relationships", body: "Toronto Custom Signs & Wraps works with North America's largest trade printers, so you get commercial-grade quality at a local price." },
  { title: "Same-day production", body: "Most orders enter production the same day artwork is approved. Next-day and rush turnaround available." },
];

/**
 * Content per SinaLite category. `image` uses SinaLite's public CDN.
 * `features` maps to the "Add Value / Target Clientele / Best Practices" section.
 */
export const CATEGORY_CONTENT = {
  "Business Cards": {
    tagline: "The most-shared piece of print you'll ever own",
    description:
      "Business cards are still the most effective piece of print marketing — a tactile, memorable moment in every handshake. Choose from matte, gloss, UV, laminated, foil, and specialty finishes on 14pt to 32pt stocks.",
    image: `${CDN}/matte-business-cards.png`,
    features: [
      { title: "Add real value", body: "A great business card creates connection and keeps you top of mind after every meeting." },
      { title: "Target clientele", body: "Perfect for realtors, contractors, entrepreneurs, professionals — anyone who networks." },
      { title: "Best practices", body: "Include name, title, phone, email, website and logo in an attractive, readable format. Bleed edges and CMYK colour." },
    ],
  },
  "Postcards": {
    tagline: "Direct mail that still gets opened",
    description:
      "Postcards are the workhorse of direct marketing — high open rates, tactile, and instantly readable. Great for promotions, appointment reminders, real estate farming, and grand-opening announcements.",
    image: `${CDN}/postcards.png`,
    features: [
      { title: "Drive response", body: "Postcards see 3-5% response rates vs 1% for email — a physical piece in the mailbox stands out." },
      { title: "Target clientele", body: "Real estate agents, restaurants, gyms, dentists, salons — any local business with a service area." },
      { title: "Best practices", body: "Bold headline, a single offer, clear call to action, and mail-safe design areas per Canada Post specs." },
    ],
  },
  "Flyers": {
    tagline: "Fast, flexible, and impactful",
    description:
      "Flyers deliver a lot of information in a single sheet — perfect for menus, promotions, event pieces, price sheets, and door drops.",
    image: `${CDN}/flyers.png`,
    features: [
      { title: "High volume, low cost", body: "One of the most affordable pieces per unit — great for hand-outs and mailbox drops." },
      { title: "Target clientele", body: "Restaurants, retailers, real estate, event producers, non-profits." },
      { title: "Best practices", body: "One clear message, strong hierarchy, and a compelling call to action above the fold." },
    ],
  },
  "Brochures": {
    tagline: "Tell your full story on one sheet",
    description:
      "Bi-fold and tri-fold brochures let you present your services, menu, or product line in a compact, professional layout. Ideal for hotels, professional services, tourism and menus.",
    image: `${CDN}/postcards.png`,
    features: [
      { title: "Perceived value", body: "Folded print elevates your brand from a flyer to a full marketing piece." },
      { title: "Target clientele", body: "Hospitality, health & wellness, financial services, real estate, tourism." },
      { title: "Best practices", body: "Plan panel-by-panel; put the strongest visual on the front, contact info on the back." },
    ],
  },
  "Booklets": {
    tagline: "Catalogues, lookbooks and menus that feel premium",
    description:
      "Saddle-stitched booklets in 8pp, 12pp, 16pp+ configurations — perfect for catalogues, portfolios, wedding programs, and product menus.",
    image: `${CDN}/postcards.png`,
    features: [
      { title: "Signature piece", body: "A well-made booklet becomes a reference piece that customers keep and share." },
      { title: "Target clientele", body: "Fashion & retail brands, restaurants, event planners, real estate portfolios." },
      { title: "Best practices", body: "Design in multi-page spreads, use CMYK from start, and plan spreads in multiples of 4 for saddle-stitch." },
    ],
  },
  "Banners": {
    tagline: "Loud, proud, and built to last",
    description:
      "13oz and 18oz vinyl banners with hemmed edges and grommets — ready for indoor or outdoor use. From event signage to construction hoarding.",
    image: `${CDN}/banners.png`,
    features: [
      { title: "Weatherproof", body: "Heavy-duty vinyl stands up to wind, rain and sun for months of use." },
      { title: "Target clientele", body: "Events, trade shows, retail promotions, construction sites, sports & schools." },
      { title: "Best practices", body: "Design 300dpi at half-scale, keep text bold and readable from 20+ feet, allow room for grommets." },
    ],
  },
  "Posters": {
    tagline: "Big, beautiful, and instantly recognisable",
    description:
      "High-resolution posters printed on premium stocks — perfect for retail, entertainment, education, and interior décor.",
    image: `${CDN}/banners.png`,
    features: [
      { title: "Wall presence", body: "A poster is an anchor piece in any room — it draws the eye and holds attention." },
      { title: "Target clientele", body: "Music & entertainment, film, education, retail displays, art & design." },
      { title: "Best practices", body: "300dpi source files, CMYK colour, at least 0.125\" bleed on each edge." },
    ],
  },
  "Stickers": {
    tagline: "Small print, big personality",
    description:
      "Die-cut and roll stickers on durable vinyl — perfect for branding, packaging, giveaways, and product labels.",
    image: `${CDN}/matte-business-cards.png`,
    features: [
      { title: "Portable branding", body: "Stickers travel with your customers — laptops, water bottles, phones, walls." },
      { title: "Target clientele", body: "Product brands, cafés, breweries, streetwear, music, giveaways." },
      { title: "Best practices", body: "Vector art (SVG/AI/PDF) works best; add a 0.125\" bleed for die-cut shapes." },
    ],
  },
  "Signs": {
    tagline: "Rigid signs, built to be seen",
    description:
      "Foam board, coroplast, styrene, and aluminum composite signs — from lawn signs and A-frames to full storefront signage.",
    image: OWNER_SIGN,
    features: [
      { title: "Durable & rigid", body: "Suitable for indoor and outdoor use with weather-resistant inks." },
      { title: "Target clientele", body: "Real estate, contractors, retail, restaurants, events, political campaigns." },
      { title: "Best practices", body: "Consider viewing distance when sizing text; include grommets or stakes for outdoor use." },
    ],
  },
};

// Fallback for categories not explicitly mapped
export const DEFAULT_CATEGORY = {
  tagline: "Professional print, expertly produced",
  description:
    "Configure your order online and we'll handle production and shipping. Live pricing, artwork review by a real person, and Canada-wide delivery.",
  image: `${CDN}/bc-aq.png`,
  features: [
    { title: "Made for professionals", body: "Trade-quality print at reseller pricing." },
    { title: "Fast turnaround", body: "Most jobs enter production the same day artwork is approved." },
    { title: "Free artwork check", body: "Our team catches bleed, resolution and colour issues before we print." },
  ],
};

/**
 * SKU/name based image overrides for individual products.
 * For products whose SKU / name matches a specific SinaLite image, override
 * the category-level fallback.
 */
const PRODUCT_IMAGE_MAP = {
  "businesscard_14pt_aq": `${CDN}/bc-aq.png`,
  "businesscard_14pt_matte": `${CDN}/matte-business-cards.png`,
  "businesscard_14pt_uv": `${CDN}/bc-gloss-uv-business-cards.png`,
  "businesscard_14pt_profit_maximizer": `${CDN}/bc-best-value.png`,
  "businesscard_18pt_matte_silk_lamination": `${CDN}/gloss-laminated-business-cards.png`,
  "businesscard_18pt_gloss_lamination": `${CDN}/gloss-laminated-business-cards.png`,
  "businesscard_14pt_writable": `${CDN}/enviro-business-cards.png`,
  "businesscard_foil": `${CDN}/spot-uv-business-cards.png`,
  "businesscard_spot_uv": `${CDN}/spot-uv-business-cards.png`,
};

/** Get the best image URL for a product. Order of preference:
 *   1. Owner-uploaded photo (product.owner_photo_url)
 *   2. Per-SKU curated CDN image
 *   3. Category-level image
 *   4. Global default
 */
export function getProductImage(product) {
  if (!product) return DEFAULT_CATEGORY.image;
  if (product.owner_photo_url) {
    // Relative URLs need to be resolved against the API host so <img> can load them.
    if (product.owner_photo_url.startsWith("/")) {
      return `${process.env.REACT_APP_BACKEND_URL}${product.owner_photo_url}`;
    }
    return product.owner_photo_url;
  }
  const skuKey = String(product.sku || "").toLowerCase().split("_").slice(0, 5).join("_");
  if (PRODUCT_IMAGE_MAP[skuKey]) return PRODUCT_IMAGE_MAP[skuKey];
  const cat = CATEGORY_CONTENT[product.category];
  if (cat?.image) return cat.image;
  return DEFAULT_CATEGORY.image;
}

/** Get category-level content or a smart default that references the actual category name. */
export function getCategoryContent(category) {
  if (category && CATEGORY_CONTENT[category]) return CATEGORY_CONTENT[category];
  if (!category) return DEFAULT_CATEGORY;
  const label = String(category).trim();
  return {
    tagline: `${label} — expertly produced`,
    description: `Premium ${label.toLowerCase()} printed to trade specs. Configure your options for a live wholesale price, upload your artwork, and we'll handle production and Canada-wide shipping. Free artwork review by a real human before we send anything to press.`,
    image: DEFAULT_CATEGORY.image,
    features: DEFAULT_CATEGORY.features,
  };
}

/**
 * Return the marketing content (tagline + description) to show on a product page.
 * Precedence: owner override → category-mapped content → smart generated fallback.
 */
export function getProductContent(product) {
  const cat = getCategoryContent(product?.category);
  return {
    tagline: (product?.owner_tagline || "").trim() || cat.tagline,
    description: (product?.owner_description || "").trim() || cat.description,
    features: cat.features,
    image: cat.image,
  };
}


/* -------------------------------------------------------------------------- */
/*  File Prep / Setup Guides                                                  */
/* -------------------------------------------------------------------------- */

/** Shared 3-step file prep visual — same across every SinaLite product. */
export const FILE_PREP_STEPS = [
  {
    n: 1,
    title: "Design in CMYK, 300 DPI",
    body: "Ensure the file matches the product's required dimensions. Create your artwork in CMYK 300 dpi with 1/16″ bleeds on all sides.",
  },
  {
    n: 2,
    title: "Embed images & outline text",
    body: "Make sure that all images are embedded, and that all text is outlined so fonts print exactly as designed.",
  },
  {
    n: 3,
    title: "Export as PDF with bleeds",
    body: "Export all files into PDF format with 1/16″ bleeds. One job = one PDF, even if multi-page.",
  },
];

/** Shared general guidelines — displayed in the File Prep tab. */
export const FILE_PREP_GUIDELINES = [
  "Download the setup guide for your product and use it as a template.",
  "DELETE any hidden layers not intended to print (setup guide layers, notes, etc.).",
  "Each job — including multi-page jobs — must be submitted as a single PDF with all pages the same size.",
  "Ensure proper file orientation to guarantee proper back-up. Review orientation guides carefully.",
  "Avoid borders when possible — if a border sits too close to the trim, it may look off-centre.",
  "Include 1/8″ bleed and keep important art/text inside the safety margin.",
  "Use CMYK at 300 DPI. Black text: C0 M0 Y0 K100. Rich black areas: C30 M20 Y20 K100.",
  "Embed or outline all fonts.",
];

/**
 * Turn a size option name into SinaLite's PDF setup guide URL slug.
 * SinaLite's naming rules (confirmed against sinalite.com/media/guides/pdf/*):
 *   "3.5 x 2"    → "35_x_2"
 *   "8.5 x 11"   → "85_x_11"
 *   "5.5 x 8.5"  → "55_x_85"
 *   "12 x 18"    → "12_x_18"
 * Decimals stripped, spaces normalised, `x` in the middle.
 */
export function sizeToGuideSlug(sizeName) {
  if (!sizeName) return null;
  const clean = String(sizeName)
    .replace(/["″”]/g, "")           // strip inch marks
    .replace(/\./g, "")              // strip decimals: 3.5 -> 35
    .trim()
    .replace(/\s*x\s*/i, "_x_");     // normalise "x" separator
  return clean || null;
}

/**
 * Get the list of Setup Guide PDF downloads for a product based on its
 * size options + whether it supports rounded corners / has vertical layouts.
 *
 * Rule (documented so it applies to EVERY new product automatically):
 *   1. For each size option in the product, generate `{slug}.pdf`.
 *   2. If the product has a "Round Corners" option group with a YES entry,
 *      also generate `{slug}_Rounded.pdf`.
 *   3. For Business Cards, always add the vertical business-card template.
 *   4. All URLs hosted on SinaLite's public CDN — no client hosting required.
 */
export function getSetupGuides(product) {
  if (!product) return [];
  const groups = (product.options && product.options.option_groups) || {};
  const sizes = groups.size || groups.Size || [];
  const hasRoundedCorners = Object.entries(groups).some(([g, opts]) => (
    /round/i.test(g) && (opts || []).some((o) => /yes/i.test(o.name))
  ));

  const guides = [];
  const seen = new Set();

  for (const opt of sizes) {
    const slug = sizeToGuideSlug(opt.name);
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    guides.push({
      title: opt.name,
      url: `https://sinalite.com/media/guides/pdf/${slug}.pdf`,
      kind: "size",
    });
    if (hasRoundedCorners) {
      const roundedSlug = `${slug}_Rounded`;
      if (!seen.has(roundedSlug)) {
        seen.add(roundedSlug);
        guides.push({
          title: `${opt.name} · rounded corners`,
          url: `https://sinalite.com/media/guides/pdf/${roundedSlug}.pdf`,
          kind: "rounded",
        });
      }
    }
  }

  // Business Card vertical layout template
  if (product.category === "Business Cards") {
    guides.push({
      title: "Vertical business card layout",
      url: "https://sinalite.com/media/guides/pdf/vertical-bc-tempalte.pdf",
      kind: "vertical",
    });
  }

  return guides;
}

/**
 * Build a Details spec table from the product's option groups + category content.
 * Returns an array of {label, value} rows suitable for a two-column table.
 */
export function getProductSpecs(product) {
  const groups = (product.options && product.options.option_groups) || {};
  const rows = [];

  const paperGroup = groups.Stock || groups.stock || groups.Paper || null;
  if (paperGroup && paperGroup.length) {
    rows.push({ label: "Paper Type / Stock", value: paperGroup.map((o) => o.name).join(" · ") });
  }
  const coating = groups.Coating || groups.coating;
  if (coating && coating.length) {
    rows.push({ label: "Coating / Finish", value: coating.map((o) => o.name).join(" · ") });
  }
  const sides = groups.Sides || groups.sides || null;
  if (sides && sides.length) {
    rows.push({ label: "Print sides", value: sides.map((o) => o.name).join(" · ") });
  } else if (paperGroup) {
    rows.push({ label: "Colour", value: "Full colour CMYK: 1-sided (4/0) or 2-sided (4/4)" });
  }
  const qty = groups.qty || groups.Quantity || groups.quantity;
  if (qty && qty.length) {
    const first = qty[0]?.name;
    const last = qty[qty.length - 1]?.name;
    rows.push({ label: "Quantities", value: first === last ? first : `${first} — ${last}` });
  }
  const sizes = groups.size || groups.Size;
  if (sizes && sizes.length) {
    rows.push({ label: "Sizes", value: sizes.map((o) => `${o.name}″`).join(" · ") });
  }
  const turnaround = groups.Turnaround || groups.turnaround;
  if (turnaround && turnaround.length) {
    rows.push({ label: "Turnaround", value: turnaround.map((o) => o.name).join(" · ") });
  }
  rows.push({ label: "Finishing", value: "Cut to size and boxed for shipping." });
  rows.push({ label: "File type", value: "Print-ready PDF, 300 DPI, CMYK, 1/16″ bleed" });

  return rows;
}
